import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { today } from '../api/calendar'
import {
  addDayChange,
  changeLabel,
  fetchMyDayChanges,
  isGone,
  removeDayChange,
  type DayChange,
} from '../api/dayChanges'
import InfoTip from './InfoTip'

/**
 * One day that differs from her weekly pattern (D-684).
 *
 * **Between her hours and her time off, and the order is the argument.** The
 * three read weakest to strongest: a week the studio agreed to, one day that
 * amends it, and dates nobody may book at all. Folding this into the hours
 * panel above would have mixed two lifecycles — that week waits for Tonya, this
 * is live the moment she saves it — and a third heading is honest about there
 * being three different things.
 *
 * **It only adds** (D-722). It used to work both ways - blank times said *not
 * in*, and times replaced her usual hours that day. A day out is time off now,
 * and a stretch here is added to her week rather than standing in for it.
 *
 * **Silent for a parent**, who has no staff row and nothing to amend.
 */
export default function DayChangePanel({
  isStaff,
  isKiosk,
}: {
  isStaff: boolean
  /** A wall tablet is nobody in particular and sets nothing (D-233). */
  isKiosk: boolean
}) {
  const enabled = isStaff && !isKiosk

  const { data, isPending, isError } = useQuery({
    queryKey: ['my-day-changes'],
    queryFn: fetchMyDayChanges,
    enabled,
  })

  const [date, setDate] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['my-day-changes'] })

  const add = useMutation({
    mutationFn: () => addDayChange(date, from || null, to || null),
    onMutate: () => setError(null),
    onSuccess: () => {
      setDate('')
      setFrom('')
      setTo('')
      refresh()
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: removeDayChange,
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  })

  if (!enabled) return null

  const field =
    'rounded-lg border border-line-2 bg-surface-2 px-2 py-1.5 text-[13px] outline-none focus:border-accent focus:bg-surface'

  const changes = data ?? []
  const now = today()

  /*
   * <b>A date and both times</b> (D-722). Blank times used to mean "not in that
   * day"; that is time off now, so the button waits for a whole stretch rather
   * than letting a half-filled form save something she did not mean.
   */
  const ready = date !== '' && from !== '' && to !== ''

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-4">
      {/*
        <b>Named for what it is for</b> (D-720). `asked`: <i>"just one day isn't
        good"</i>; the owner chose <i>Bonus private times</i> from four. Its main
        use is a teacher staying longer on a day she is in anyway - a sub day -
        so families can book her. A day out is time off now, not blank times here
        (D-722).
      */}
      <div className="flex flex-wrap items-center gap-1">
        <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Bonus private times
        </h2>
        <InfoTip label="More about bonus private times">
          Times on one date when families can book a private with you, added to your usual hours
          for that day. To mark a day you&rsquo;re out, add it as time off. Your weekly hours
          don&rsquo;t change, and it takes effect straight away.
        </InfoTip>
      </div>

      <p className="mt-1 text-[13px] text-ink-2">Extra time on a date for families to book privates.</p>

      {isPending ? (
        <p className="mt-3 text-[13px] text-ink-3">Loading...</p>
      ) : isError ? (
        /*
         * <b>A failed read is never a statement about her data</b> (D-209). The
         * commonest cause is the ~75 seconds after a deploy when every
         * database-backed endpoint refuses (D-33), and the client's one retry is
         * spent inside a second — so falling through to the empty state would
         * tell her the Friday she just added is not there, and the obvious thing
         * to do about that is add it again.
         */
        <p
          role="alert"
          className="mt-3 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2"
        >
          Could not load your bonus private times just now. Anything you have recorded is still there
          &mdash; try again shortly.
        </p>
      ) : (
        <>
          {changes.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {changes.map((change: DayChange) => (
                <li
                  key={change.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-line-2 bg-surface-2 px-3 py-2"
                >
                  <span className="text-[13px] font-semibold text-ink">
                    {changeLabel(change)}
                  </span>

                  {/*
                    <b>A change a holiday has since covered says so</b> (D-684).

                    The entry door refuses a change on a date she is already away
                    for, because a row that silently does nothing is worse than a
                    refusal. Booking the holiday afterwards is the other
                    direction of the same pair, and refusing *that* would make
                    the hard record the weaker one — so the change is kept,
                    overridden, and told about. Removing the time off brings it
                    back, which is why it is not simply deleted.
                  */}
                  {change.overridden && (
                    <span className="rounded border border-line-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                      time off covers this
                    </span>
                  )}

                  {isGone(change, now) ? (
                    // Nothing to take back on a day that has gone, and a button
                    // whose only outcome is confusion is worse than none (D-333).
                    <span className="ml-auto text-[11.5px] text-ink-3">past</span>
                  ) : (
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(change.id)}
                      aria-label={`Remove bonus time ${changeLabel(change)}`}
                      className="ml-auto min-h-[36px] px-1.5 text-[12px] font-semibold text-ink-3 underline underline-offset-2 hover:text-crit disabled:opacity-60"
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {changes.length === 0 && (
            <p className="mt-3 text-[12.5px] text-ink-3">
              None added. Your usual hours apply to every date.
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">Which day</span>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  setError(null)
                  setDate(e.target.value)
                }}
                className={field}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">From</span>
              <input
                type="time"
                value={from}
                onChange={(e) => {
                  setError(null)
                  setFrom(e.target.value)
                }}
                className={field}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">Until</span>
              <input
                type="time"
                value={to}
                onChange={(e) => {
                  setError(null)
                  setTo(e.target.value)
                }}
                className={field}
              />
            </label>

            <button
              type="button"
              disabled={!ready || add.isPending}
              onClick={() => add.mutate()}
              className="min-h-[36px] rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-60"
            >
              {add.isPending ? 'Adding...' : 'Add'}
            </button>
          </div>

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit"
            >
              {error}
            </p>
          )}
        </>
      )}
    </section>
  )
}
